import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CloudUploadIcon, ChevronDownIcon, ChevronRightIcon, CheckCircleIcon } from '../../assets/icons';
import Layout from '../../components/Layout';
import AddRecipientWizardShell, { WizardFormFooter } from '../../components/AddRecipientWizard';
import { PARSE_DOCUMENT_ACCEPT } from '../../utils/documentUploadHelpers';
import DynamicRecipientForm from '../../components/DynamicRecipientForm';
import { getRecipientRequirements } from '../../store/sagas/api';
import { buildRequestObject, getFieldPath, getNestedValue } from '../../utils/recipientRequirements';
import { buildRecipientAutoFillFromUser, canAutoFillRecipientFromUser } from '../../utils/recipientAutoFillHelpers';
import * as recipientsActions from '../../store/actions/recipients';
import ErrorToast from '../../components/ErrorToast';
import SuccessToast from '../../components/SuccessToast';
import { UserType } from '../../enums/index';

function AddRecipientForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const currency = searchParams.get('currency');
  const recipientType = searchParams.get('recipientType');
  const rail = searchParams.get('rail');
  const autoPopulate = searchParams.get('autoPopulate') === 'true';
  const isEditMode = searchParams.get('edit') === 'true';
  const editRecipientId = searchParams.get('recipientId');
  const editOffRampProvider = searchParams.get('offRampProvider');

  const {
    recipients,
    creatingRecipient,
    addRecipientError,
    updatingRecipient,
    updateRecipientError,
    parsingDocument,
    parseDocumentError,
    parsedDocumentData,
  } = useSelector((state) => state.recipients);
  const { currencies: configCurrencies } = useSelector((state) => state.config);
  const { user } = useSelector((state) => state.user);

  const [requirements, setRequirements] = useState([]);
  const [sections, setSections] = useState([]);
  const [rawSchema, setRawSchema] = useState(null);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [requirementsError, setRequirementsError] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  const [aiUploaderExpanded, setAiUploaderExpanded] = useState(false);
  const [aiUploadFileName, setAiUploadFileName] = useState('');
  const [parseSuccessInfo, setParseSuccessInfo] = useState(null);
  const [uploadValidationError, setUploadValidationError] = useState(null);
  const lastFetchedRef = useRef({ bankCountryCode: null, currencyCode: null });
  const prevCreatingRef = useRef(null);
  const prevUpdatingRef = useRef(null);

  const editRecipient = useMemo(() => {
    if (!isEditMode || !editRecipientId) return null;
    const list = Array.isArray(recipients) ? recipients : [];
    return list.find((r) => String(r.id) === String(editRecipientId)) || null;
  }, [isEditMode, editRecipientId, recipients]);

  const getCountryFromCurrency = (currencyCode) => {
    if (!currencyCode || !configCurrencies || configCurrencies.length === 0) return null;

    const normalizedCurrency = currencyCode.toUpperCase();
    const currency = configCurrencies.find((c) => c.code === normalizedCurrency);
    if (!currency) return null;

    if (currency.country && currency.country.code) {
      return currency.country.code;
    }

    if (currency.countries && Array.isArray(currency.countries) && currency.countries.length > 0) {
      return currency.countries[0].code;
    }

    return null;
  };

  const extractSelectedValues = (fields, sections = []) => {
    const selectedValues = {};
    fields.forEach((field) => {
      if (field.selected !== undefined && field.selected !== null && field.selected !== '') {
        const fieldKey =
          field.key ||
          (field.field ? getFieldPath(field.field) : '') ||
          (field.data ? getFieldPath(field.data) : '') ||
          (field.variableName ? getFieldPath(field.variableName) : '');
        if (fieldKey) {
          selectedValues[fieldKey] = field.selected;
        }
      }
    });
    sections.forEach((section) => {
      if (section.fields && Array.isArray(section.fields)) {
        section.fields.forEach((field) => {
          if (field.selected !== undefined && field.selected !== null && field.selected !== '') {
            const fieldKey =
              field.key ||
              (field.field ? getFieldPath(field.field) : '') ||
              (field.data ? getFieldPath(field.data) : '') ||
              (field.variableName ? getFieldPath(field.variableName) : '');
            if (fieldKey) {
              selectedValues[fieldKey] = field.selected;
            }
          }
        });
      }
    });

    return selectedValues;
  };

  const normalizeRegex = (regexPattern) => {
    if (!regexPattern || typeof regexPattern !== 'string') {
      return regexPattern;
    }
    let normalized = regexPattern;
    normalized = normalized.replace(/([^\\])d\{/g, '$1\\d{');
    normalized = normalized.replace(/^d\{/g, '\\d{');
    normalized = normalized.replace(/([^\\])D\{/g, '$1\\D{');
    normalized = normalized.replace(/^D\{/g, '\\D{');
    normalized = normalized.replace(/([^\\])s\{/g, '$1\\s{');
    normalized = normalized.replace(/^s\{/g, '\\s{');
    normalized = normalized.replace(/([^\\])S\{/g, '$1\\S{');
    normalized = normalized.replace(/^S\{/g, '\\S{');
    normalized = normalized.replace(/([^\\])w\{/g, '$1\\w{');
    normalized = normalized.replace(/^w\{/g, '\\w{');
    normalized = normalized.replace(/([^\\])W\{/g, '$1\\W{');
    normalized = normalized.replace(/^W\{/g, '\\W{');
    return normalized;
  };

  useEffect(() => {
    if (!currency || !recipientType) {
      navigate('/select-currency');
      return;
    }

    const bankCountryCode = getCountryFromCurrency(currency);
    const fetchKey = `${bankCountryCode || 'none'}_${currency}_${recipientType}_${rail || ''}`;
    const lastFetchKey = `${lastFetchedRef.current.bankCountryCode || 'none'}_${lastFetchedRef.current.currencyCode}_${lastFetchedRef.current.recipientType}_${lastFetchedRef.current.rail || ''}`;

    if (fetchKey === lastFetchKey && !loadingRequirements) {
      return;
    }

    setLoadingRequirements(true);
    setRequirementsError(null);
    lastFetchedRef.current = { bankCountryCode, currencyCode: currency, recipientType, rail };

    const recipientTypeParam = recipientType === 'individual' ? 'INDIVIDUAL' : 'BUSINESS';

    const fetchRequirements = async () => {
      try {
        const data = await getRecipientRequirements(currency, bankCountryCode);
        let extractedFields = [];
        const extractedSections = [];

        if (data && data.rails && Array.isArray(data.rails)) {
          const matchingRail = rail ? data.rails.find((r) => r.name && r.name.toLowerCase() === rail.toLowerCase()) : data.rails[0];

          if (matchingRail && matchingRail.sections && Array.isArray(matchingRail.sections)) {
            const recipientTypeLower = recipientType === 'individual' ? 'individual' : 'company';

            matchingRail.sections.forEach((section) => {
              const sectionTypes = section.types || [];
              const supportsType =
                sectionTypes.length === 0 ||
                sectionTypes.some((t) => {
                  const typeLower = typeof t === 'string' ? t.toLowerCase() : t;
                  return typeLower === recipientTypeLower;
                });

              if (supportsType && section.fields && Array.isArray(section.fields)) {
                extractedFields.push(...section.fields);
                extractedSections.push({
                  name: section.name,
                  fields: section.fields,
                });
              }
            });
          }
        } else if (Array.isArray(data) && data.length > 0) {
          extractedFields = data.filter((req) => {
            if (rail && req.rails && Array.isArray(req.rails) && req.rails.length > 0) {
              const railMatches = req.rails.some((r) => r && r.toLowerCase() === rail.toLowerCase());
              if (!railMatches) return false;
            }

            if (req.accountHolderTypes && Array.isArray(req.accountHolderTypes) && req.accountHolderTypes.length > 0) {
              const typeMatches = req.accountHolderTypes.includes(recipientTypeParam);
              if (!typeMatches) return false;
            }

            return true;
          });
        }

        setRequirements(extractedFields);
        setSections(extractedSections);
        setRawSchema(data);
        const selectedValues = extractSelectedValues(extractedFields, extractedSections);
        const initialFormData = { ...selectedValues };

        if (autoPopulate && user && extractedFields.length > 0) {
          Object.assign(
            initialFormData,
            buildRecipientAutoFillFromUser(user, recipientType, extractedFields, extractedSections),
          );
        }

        setFormData(initialFormData);
        setLoadingRequirements(false);
      } catch (error) {
        if (!bankCountryCode && (currency?.toUpperCase() === 'USD' || currency?.toUpperCase() === 'MXN')) {
          try {
            const data = await getRecipientRequirements(currency, null);
            let extractedFields = [];

            if (data && data.rails && Array.isArray(data.rails)) {
              const matchingRail = rail ? data.rails.find((r) => r.name && r.name.toLowerCase() === rail.toLowerCase()) : data.rails[0];

              if (matchingRail && matchingRail.sections && Array.isArray(matchingRail.sections)) {
                const recipientTypeLower = recipientType === 'individual' ? 'individual' : 'company';

                matchingRail.sections.forEach((section) => {
                  const sectionTypes = section.types || [];
                  const supportsType =
                    sectionTypes.length === 0 ||
                    sectionTypes.some((t) => {
                      const typeLower = typeof t === 'string' ? t.toLowerCase() : t;
                      return typeLower === recipientTypeLower;
                    });

                  if (supportsType && section.fields && Array.isArray(section.fields)) {
                    extractedFields.push(...section.fields);
                  }
                });
              }
            } else if (Array.isArray(data) && data.length > 0) {
              extractedFields = data.filter((req) => {
                if (rail && req.rails && Array.isArray(req.rails) && req.rails.length > 0) {
                  const railMatches = req.rails.some((r) => r && r.toLowerCase() === rail.toLowerCase());
                  if (!railMatches) return false;
                }

                if (req.accountHolderTypes && Array.isArray(req.accountHolderTypes) && req.accountHolderTypes.length > 0) {
                  const typeMatches = req.accountHolderTypes.includes(recipientTypeParam);
                  if (!typeMatches) return false;
                }

                return true;
              });
            }

            setRequirements(extractedFields);
            const extractedSectionsRetry = [];
            if (data && data.rails && Array.isArray(data.rails)) {
              const matchingRail = rail ? data.rails.find((r) => r.name && r.name.toLowerCase() === rail.toLowerCase()) : data.rails[0];
              if (matchingRail && matchingRail.sections && Array.isArray(matchingRail.sections)) {
                const recipientTypeLower = recipientType === 'individual' ? 'individual' : 'company';
                matchingRail.sections.forEach((section) => {
                  const sectionTypes = section.types || [];
                  const supportsType =
                    sectionTypes.length === 0 ||
                    sectionTypes.some((t) => {
                      const typeLower = typeof t === 'string' ? t.toLowerCase() : t;
                      return typeLower === recipientTypeLower;
                    });
                  if (supportsType && section.fields && Array.isArray(section.fields)) {
                    extractedSectionsRetry.push({
                      name: section.name,
                      fields: section.fields,
                    });
                  }
                });
              }
            }
            setSections(extractedSectionsRetry);
            setRawSchema(data);
            const selectedValuesRetry = extractSelectedValues(extractedFields, extractedSectionsRetry);
            setFormData(selectedValuesRetry);
            setLoadingRequirements(false);
          } catch (retryError) {
            setRequirementsError(retryError);
            setRequirements([]);
            setSections([]);
            setRawSchema(null);
            setLoadingRequirements(false);
            lastFetchedRef.current = { bankCountryCode: null, currencyCode: null, recipientType: null, rail: null };
          }
        } else {
          setRequirementsError(error);
          setRequirements([]);
          setSections([]);
          setRawSchema(null);
          setLoadingRequirements(false);
          lastFetchedRef.current = { bankCountryCode: null, currencyCode: null, recipientType: null, rail: null };
        }
      }
    };

    fetchRequirements();
  }, [currency, recipientType, rail, configCurrencies, autoPopulate, user, navigate]);

  useEffect(() => {
    if (!isEditMode || !editRecipient || requirements.length === 0) return;

    const baseMapping = {
      displayName: editRecipient.displayName || editRecipient.name,
      name: editRecipient.name,
      email: editRecipient.email || editRecipient.emailAddress || editRecipient.contactEmail,
      iban: editRecipient.iban,
      bic: editRecipient.bic,
      accountNumber: editRecipient.accountNumber || editRecipient.bankAccountNumber || editRecipient.accountId,
      bankName:
        editRecipient.bankName || editRecipient.bank?.name || editRecipient.financialInstitution || editRecipient.institutionName,
      countryCode: editRecipient.countryCode || editRecipient.bankCountryCode || editRecipient.country?.code,
    };

    const next = {};
    requirements.forEach((req) => {
      const fieldPath = req.key || (req.field ? req.field : '') || (req.data ? req.data : '') || (req.variableName ? req.variableName : '');
      if (!fieldPath) return;
      const flatKey = getFieldPath(fieldPath);

      if (editRecipient[flatKey] != null && editRecipient[flatKey] !== '') {
        next[flatKey] = editRecipient[flatKey];
        return;
      }

      if (baseMapping[flatKey] != null && baseMapping[flatKey] !== '') {
        next[flatKey] = baseMapping[flatKey];
      }

      if (flatKey === 'companyName' && (editRecipient.companyName || editRecipient.name)) {
        next.companyName = editRecipient.companyName || editRecipient.name;
      }
    });

    setFormData((prev) => ({ ...next, ...prev }));
  }, [isEditMode, editRecipient, requirements]);

  const handleFormChange = (fieldKey, value) => {
    if (fieldKey === 'displayName') {
      setDisplayNameManuallyEdited(true);
    }

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [fieldKey]: value,
      };

      const normalizedFieldKey = fieldKey.replace(/[._-]/g, '').toLowerCase();
      const isIndividualNameField =
        normalizedFieldKey.endsWith('firstname') ||
        normalizedFieldKey.endsWith('middlename') ||
        normalizedFieldKey.endsWith('lastname');

      if (!displayNameManuallyEdited && isIndividualNameField) {
        const readNamePart = (suffix) => {
          const directKey = Object.keys(newFormData).find((key) => key.replace(/[._-]/g, '').toLowerCase().endsWith(suffix));
          const fromNew = directKey ? newFormData[directKey] : '';
          const fromPrev = Object.keys(prev).find((key) => key.replace(/[._-]/g, '').toLowerCase().endsWith(suffix));
          return (fromNew || (fromPrev ? prev[fromPrev] : '') || '').toString().trim();
        };

        const parts = [readNamePart('firstname'), readNamePart('middlename'), readNamePart('lastname')].filter(Boolean);

        if (parts.length > 0) {
          const fullName = parts.join(' ').trim();
          Object.keys(newFormData).forEach((key) => {
            const normalized = key.replace(/[._-]/g, '').toLowerCase();
            if (normalized.endsWith('displayname') || normalized === 'beneficiaryname' || normalized.endsWith('beneficiaryname')) {
              newFormData[key] = fullName;
            }
          });
          if (!Object.keys(newFormData).some((key) => key.replace(/[._-]/g, '').toLowerCase().endsWith('displayname'))) {
            newFormData.displayName = fullName;
          }
        }
      }

      if (!displayNameManuallyEdited && (fieldKey === 'companyName' || normalizedFieldKey.endsWith('companyname'))) {
        const companyKey =
          fieldKey === 'companyName'
            ? fieldKey
            : Object.keys(newFormData).find((key) => key.replace(/[._-]/g, '').toLowerCase().endsWith('companyname'));
        const companyName = (companyKey ? newFormData[companyKey] : newFormData.companyName ?? prev.companyName ?? '')
          .toString()
          .trim();
        Object.keys(newFormData).forEach((key) => {
          const normalized = key.replace(/[._-]/g, '').toLowerCase();
          if (normalized.endsWith('displayname') || normalized === 'beneficiaryname' || normalized.endsWith('beneficiaryname')) {
            newFormData[key] = companyName;
          }
        });
        if (!Object.keys(newFormData).some((key) => key.replace(/[._-]/g, '').toLowerCase().endsWith('displayname'))) {
          newFormData.displayName = companyName || newFormData.displayName || '';
        }
      }

      return newFormData;
    });

    if (formErrors[fieldKey]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    requirements.forEach((req) => {
      const fieldKey =
        req.key ||
        (req.field ? getFieldPath(req.field) : '') ||
        (req.data ? getFieldPath(req.data) : '') ||
        (req.variableName ? getFieldPath(req.variableName) : '');
      const value = formData[fieldKey];

      if (req.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors[fieldKey] = `${req.label || fieldKey} is required`;
      }

      if (value && req.regex) {
        try {
          const normalizedRegex = normalizeRegex(req.regex);
          const regex = new RegExp(normalizedRegex);
          if (!regex.test(value)) {
            errors[fieldKey] = `Please match the requested format`;
          }
        } catch (regexError) {
          // eslint-disable-line no-empty
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (addRecipientError) {
      dispatch(recipientsActions.clearAddRecipientError());
    }
    if (updateRecipientError) {
      dispatch({ type: recipientsActions.UPDATE_RECIPIENT_ERROR, payload: null });
    }

    if (!currency) {
      dispatch({
        type: recipientsActions.ADD_RECIPIENT_ERROR,
        payload: {
          message: 'Currency is required',
          name: 'Validation Error',
        },
      });
      return;
    }

    const requestData = buildRequestObject(requirements, formData);

    const hasAccountClassRequirement = requirements.some((r) => {
      const p =
        r.key ||
        (r.field ? getFieldPath(r.field) : '') ||
        (r.data ? getFieldPath(r.data) : '') ||
        (r.variableName ? getFieldPath(r.variableName) : '');
      return p === 'account_class' || p === 'accountClass';
    });
    if (hasAccountClassRequirement) {
      const accountClassValue = requestData.account_class ?? requestData.accountClass ?? formData.account_class ?? formData.accountClass;
      if (accountClassValue != null && accountClassValue !== '') {
        requestData.account_class = accountClassValue;
      } else {
        requestData.account_class = recipientType === 'individual' ? 'individual' : 'business';
      }
      if (requestData.accountClass !== undefined) {
        delete requestData.accountClass;
      }
    }

    if (isEditMode) {
      dispatch(
        recipientsActions.updateRecipient({
          offRampProvider: editOffRampProvider || editRecipient?.offRampProvider,
          recipientId: editRecipientId,
          data: requestData,
        }),
      );
      return;
    }

    dispatch(
      recipientsActions.addRecipient({
        currencyCode: currency,
        recipientType,
        rail,
        data: requestData,
      }),
    );
  };

  useEffect(() => {
    if (prevCreatingRef.current && !creatingRecipient && !addRecipientError) {
      setShowSuccessToast(true);
      setTimeout(() => {
        navigate('/recipients');
      }, 2000);
    }
    prevCreatingRef.current = creatingRecipient;
  }, [creatingRecipient, addRecipientError, navigate]);

  useEffect(() => {
    if (prevUpdatingRef.current && !updatingRecipient && !updateRecipientError) {
      setShowSuccessToast(true);
      setTimeout(() => {
        navigate(editRecipientId ? `/recipients/${encodeURIComponent(editRecipientId)}` : '/recipients');
      }, 1200);
    }
    prevUpdatingRef.current = updatingRecipient;
  }, [updatingRecipient, updateRecipientError, navigate, editRecipientId]);

  useEffect(() => {
    if (!parsedDocumentData || requirements.length === 0) return;
    const extracted = parsedDocumentData.extracted_data;
    const mapped = {};
    if (extracted && typeof extracted === 'object') {
      requirements.forEach((req) => {
        const fieldPath = req.key || (req.field ? req.field : '') || (req.data ? req.data : '') || (req.variableName ? req.variableName : '');
        if (!fieldPath) return;
        const flatKey = getFieldPath(fieldPath);
        const v = getNestedValue(extracted, fieldPath) ?? extracted[flatKey] ?? extracted[fieldPath];
        if (v != null && v !== '') mapped[flatKey] = v;
      });
    }
    const fieldsCount = Object.keys(mapped).length;
    if (fieldsCount > 0) {
      setFormData((prev) => ({ ...prev, ...mapped }));
    }
    setParseSuccessInfo({ fileName: aiUploadFileName || 'Document', fieldsCount });
    setAiUploadFileName('');
    dispatch(recipientsActions.clearParseRecipientDocument());
  }, [parsedDocumentData, requirements, dispatch, aiUploadFileName]);

  const handleAiDocumentUpload = (file) => {
    if (!file) return;
    setParseSuccessInfo(null);
    setAiUploadFileName(file.name);
    dispatch(
      recipientsActions.parseRecipientDocument({
        file,
        currencyCode: currency || '',
        userType: recipientType,
        rail: rail || '',
        schema: rawSchema,
      }),
    );
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleAiDocumentUpload(file);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) handleAiDocumentUpload(file);
  };

  const getParseSuccessMessage = (info) => {
    if (info.fieldsCount > 0) {
      const fieldWord = info.fieldsCount === 1 ? 'field' : 'fields';
      return `Document parsed successfully. ${info.fieldsCount} ${fieldWord} populated.`;
    }
    return 'Document parsed successfully but no bank details could be extracted.';
  };

  const renderUploadContent = () => {
    if (parsingDocument) {
      return (
        <>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2' />
          <p className='text-sm text-primary font-medium'>Analysing with AI</p>
        </>
      );
    }
    if (parseSuccessInfo) {
      return (
        <>
          <CheckCircleIcon className='h-8 w-8 text-green-600 mb-2' />
          <p className='text-sm text-foreground font-medium'>{parseSuccessInfo.fileName}</p>
          <p className='text-xs text-muted-foreground mt-1'>{getParseSuccessMessage(parseSuccessInfo)}</p>
        </>
      );
    }
    return (
      <>
        <CloudUploadIcon className='h-8 w-8 text-muted-foreground mb-2' />
        {aiUploadFileName ? (
          <p className='text-sm text-foreground font-medium'>{aiUploadFileName}</p>
        ) : (
          <p className='text-sm text-muted-foreground'>Click here or drag file to upload</p>
        )}
      </>
    );
  };

  const handleCopyFromUser = () => {
    if (!user || !canAutoFillRecipientFromUser(user, recipientType)) return;

    const autoFillData = buildRecipientAutoFillFromUser(user, recipientType, requirements, sections);
    if (Object.keys(autoFillData).length === 0) return;

    setDisplayNameManuallyEdited(false);
    setFormData((prev) => ({
      ...prev,
      ...autoFillData,
    }));
  };

  const allRequiredFieldsFilled = requirements.every((req) => {
    if (!req.required) return true;
    const fieldKey = req.key || (req.field ? getFieldPath(req.field) : '') || (req.variableName ? getFieldPath(req.variableName) : '');
    const value = formData[fieldKey];
    return value && (typeof value !== 'string' || value.trim() !== '');
  });

  const canSubmit = requirements.length > 0 && !loadingRequirements && allRequiredFieldsFilled && Object.keys(formErrors).length === 0;

  const isSubmitting = creatingRecipient || updatingRecipient;
  let submitButtonLabel = isEditMode ? 'Save changes' : 'Add recipient';
  if (isSubmitting) {
    submitButtonLabel = isEditMode ? 'Saving...' : 'Adding...';
  }

  const wizardTitle = (() => {
    if (isEditMode) return 'Edit recipient';
    if (recipientType === 'company') return 'Add a business';
    return 'Add an individual';
  })();

  const wizardSubtitle = isEditMode
    ? 'Update the recipient details below.'
    : 'Enter the account and identity details required for this payment route.';

  const fromRailPicker = searchParams.get('fromRailPicker') === 'true';

  const handleBack = () => {
    if (isEditMode && editRecipientId) {
      navigate(`/recipients/${editRecipientId}`);
      return;
    }

    if (fromRailPicker && currency && recipientType) {
      const params = new URLSearchParams({ currency, recipientType });
      if (autoPopulate) {
        params.set('autoPopulate', 'true');
      }
      navigate(`/select-rail?${params.toString()}`);
      return;
    }

    navigate(`/recipient-type?currency=${currency || ''}`);
  };

  const handleCancel = () => {
    if (isEditMode && editRecipientId) {
      navigate(`/recipients/${editRecipientId}`);
      return;
    }
    navigate('/recipients');
  };

  return (
    <Layout
      hideHeader
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={{ onClick: handleBack, label: 'Back', ariaLabel: 'Back to previous step' }}
    >
      <AddRecipientWizardShell currentStep={3} title={wizardTitle} subtitle={wizardSubtitle}>
        {loadingRequirements && (
          <div className='py-8 text-center'>
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>Loading form fields...</p>
          </div>
        )}

        {!loadingRequirements && requirements.length > 0 && (
          <form onSubmit={handleSubmit}>
            <div className='mb-6'>
              <button
                type='button'
                onClick={() => {
                  const next = !aiUploaderExpanded;
                  setAiUploaderExpanded(next);
                  if (next) {
                    dispatch(recipientsActions.clearParseRecipientDocument());
                    setAiUploadFileName('');
                    setParseSuccessInfo(null);
                    setUploadValidationError(null);
                  }
                }}
                className='flex w-full items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3.5 py-3 text-left text-sm font-medium text-[var(--n800)] transition-colors hover:bg-[var(--off)] font-[family-name:var(--font-ui)]'
              >
                <span>Add with AI through an invoice or screenshot</span>
                {aiUploaderExpanded ? <ChevronDownIcon className='h-4 w-4' /> : <ChevronRightIcon className='h-4 w-4' />}
              </button>
              {aiUploaderExpanded && (
                <div className='mt-2 space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--off)] p-4'>
                  <label htmlFor='ai-document-upload' className='mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                    Upload document
                  </label>
                  <div onDragOver={handleDragOver} onDrop={handleDrop} className='relative'>
                    <input
                      type='file'
                      id='ai-document-upload'
                      accept={PARSE_DOCUMENT_ACCEPT}
                      onChange={handleFileInputChange}
                      className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
                      disabled={parsingDocument}
                    />
                    <div className='flex h-32 w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-[var(--color-border)] bg-[var(--white)] transition-colors hover:bg-[var(--off)]'>
                      {renderUploadContent()}
                    </div>
                  </div>
                  <p className='text-[11.5px] leading-snug text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                    Upload an invoice or screenshot containing bank account details. The AI will extract and populate the form fields automatically.
                  </p>
                  {(uploadValidationError || parseDocumentError) && (
                    <p className='text-[11.5px] text-[var(--color-error)] font-[family-name:var(--font-ui)]'>
                      {uploadValidationError || parseDocumentError.message || 'Parse failed. The endpoint may not be ready yet.'}
                    </p>
                  )}
                </div>
              )}
            </div>

            {user && canAutoFillRecipientFromUser(user, recipientType) && (
                <div className='mb-4 flex justify-end'>
                  <button
                    type='button'
                    onClick={handleCopyFromUser}
                    className='text-sm font-medium text-[var(--n800)] underline decoration-[var(--n400)] underline-offset-2 hover:text-[var(--n900)] font-[family-name:var(--font-ui)]'
                  >
                    {user.userType === UserType.Individual ? 'To myself' : 'To my company'}
                  </button>
                </div>
              )}

            <DynamicRecipientForm
              sections={sections}
              requirements={requirements}
              formData={formData}
              onChange={handleFormChange}
              errors={formErrors}
              variant='wizard'
            />

            <WizardFormFooter
              onCancel={handleCancel}
              continueLabel={submitButtonLabel}
              continueDisabled={!canSubmit || isSubmitting}
              submit
            />
          </form>
        )}

        {!loadingRequirements && requirements.length === 0 && !requirementsError && (
          <div className='py-8 text-center'>
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>No fields available for this configuration.</p>
          </div>
        )}
      </AddRecipientWizardShell>

      {requirementsError && <ErrorToast error={requirementsError} onDismiss={() => setRequirementsError(null)} />}
      {addRecipientError && <ErrorToast error={addRecipientError} onDismiss={() => dispatch(recipientsActions.clearAddRecipientError())} />}
      {updateRecipientError && (
        <ErrorToast error={updateRecipientError} onDismiss={() => dispatch({ type: recipientsActions.UPDATE_RECIPIENT_ERROR, payload: null })} />
      )}
      {showSuccessToast && (
        <SuccessToast
          message={isEditMode ? 'Recipient updated successfully!' : 'Recipient added successfully!'}
          onDismiss={() => setShowSuccessToast(false)}
        />
      )}
    </Layout>
  );
}

export default AddRecipientForm;
